'use client';
import React, { useState } from 'react';
import { Stethoscope, Rocket, Scale, Crown, Star, Lock, PlayCircle, BookOpen, Clock, Search, Calendar } from 'lucide-react';
import Modal from '@/components/ui/Modal';

type LibraryCategory = 'all' | 'specialized' | 'premium' | 'seasonal' | 'signature';

interface LibraryCourse {
  id: string;
  title: string;
  category: LibraryCategory;
  specialization?: string;
  professor?: string;
  professorTitle?: string;
  price?: number;
  isFree: boolean;
  duration: string;
  lessons: number;
  students: number;
  rating: number;
  tags: string[];
  description: string;
  locked: boolean;
  seasonal?: string;
  badge?: string;
  badgeColor?: string;
}

const libraryCourses: LibraryCourse[] = [
  {
    id: 'lib-001', title: 'Medical Diagnostics & Clinical Reasoning', category: 'specialized', specialization: 'Medical',
    isFree: false, price: 299, duration: '14 weeks', lessons: 42, students: 1840, rating: 4.9,
    tags: ['Medicine', 'Diagnostics', 'Clinical'], description: 'Advanced clinical reasoning, differential diagnosis, and evidence-based medicine for healthcare professionals.',
    locked: true, badge: 'Medical', badgeColor: 'text-red-600 bg-red-50 border-red-200',
  },
  {
    id: 'lib-002', title: 'Aerospace Mission Design', category: 'specialized', specialization: 'Aerospace',
    isFree: false, price: 349, duration: '16 weeks', lessons: 48, students: 620, rating: 4.8,
    tags: ['Aerospace', 'Mission Design', 'Orbital Mechanics'], description: 'End-to-end spacecraft mission design from concept to launch — propulsion, guidance, and systems engineering.',
    locked: true, badge: 'Aerospace', badgeColor: 'text-blue-600 bg-blue-50 border-blue-200',
  },
  {
    id: 'lib-003', title: 'Applied Ethics in Technology', category: 'specialized', specialization: 'Ethics',
    isFree: true, duration: '6 weeks', lessons: 18, students: 5200, rating: 4.7,
    tags: ['Ethics', 'Technology', 'Philosophy'], description: 'Frameworks for ethical decision-making in AI, biotech, and emerging technologies. Case studies from real-world dilemmas.',
    locked: false, badge: 'Ethics', badgeColor: 'text-purple-600 bg-purple-50 border-purple-200',
  },
  {
    id: 'lib-004', title: 'Quantum Computing Mastery', category: 'premium',
    professor: 'Dr. Nadia Volkov', professorTitle: 'AI Research Scientist, MIT',
    isFree: false, price: 199, duration: '12 weeks', lessons: 36, students: 890, rating: 5.0,
    tags: ['Quantum', 'Computing', 'Physics'], description: 'From qubits to quantum algorithms — a comprehensive premium track with live Q&A sessions and certification.',
    locked: true, badge: 'Premium', badgeColor: 'text-amber-600 bg-amber-50 border-amber-200',
  },
  {
    id: 'lib-005', title: 'Advanced Calculus & Real Analysis', category: 'signature',
    professor: 'Prof. Marcus Chen', professorTitle: 'Mathematics Professor, Stanford',
    isFree: false, price: 149, duration: '10 weeks', lessons: 30, students: 2100, rating: 4.9,
    tags: ['Calculus', 'Analysis', 'Mathematics'], description: 'Prof. Chen\'s signature course — rigorous real analysis with his famous "intuition-first" teaching methodology.',
    locked: true, badge: 'Signature', badgeColor: 'text-rose-600 bg-rose-50 border-rose-200',
  },
  {
    id: 'lib-006', title: 'Space Systems Engineering', category: 'signature',
    professor: 'Col. James Rivera', professorTitle: 'Mission Specialist, NASA JPL',
    isFree: false, price: 249, duration: '12 weeks', lessons: 38, students: 430, rating: 4.8,
    tags: ['Space', 'Systems', 'Engineering'], description: 'Col. Rivera\'s insider course on real NASA mission engineering — from concept review to launch readiness.',
    locked: true, badge: 'Signature', badgeColor: 'text-rose-600 bg-rose-50 border-rose-200',
  },
  {
    id: 'lib-007', title: 'Summer STEM Intensive 2026', category: 'seasonal',
    isFree: false, price: 79, duration: '8 weeks', lessons: 24, students: 3400, rating: 4.6,
    tags: ['STEM', 'Summer', 'Intensive'], description: 'A condensed summer program covering math, science, and coding — designed for students ages 12–17 during school break.',
    locked: false, seasonal: 'Summer 2026', badge: 'Seasonal', badgeColor: 'text-orange-600 bg-orange-50 border-orange-200',
  },
  {
    id: 'lib-008', title: 'AI Safety & Alignment — 2026 Edition', category: 'seasonal',
    isFree: true, duration: '4 weeks', lessons: 12, students: 7800, rating: 4.7,
    tags: ['AI Safety', 'Alignment', 'Current Events'], description: 'Updated annually with the latest AI safety research, policy developments, and alignment techniques from 2026.',
    locked: false, seasonal: 'Annual Update', badge: 'Seasonal', badgeColor: 'text-orange-600 bg-orange-50 border-orange-200',
  },
];

const categoryTabs = [
  { id: 'all' as LibraryCategory, label: 'All Libraries', icon: BookOpen },
  { id: 'specialized' as LibraryCategory, label: '🏥 Specialized', icon: Stethoscope },
  { id: 'premium' as LibraryCategory, label: '⭐ Premium', icon: Star },
  { id: 'seasonal' as LibraryCategory, label: '📅 Seasonal', icon: Calendar },
  { id: 'signature' as LibraryCategory, label: '👨‍🏫 Signature', icon: Crown },
];

const specializationIcons: Record<string, React.ElementType> = {
  Medical: Stethoscope, Aerospace: Rocket, Ethics: Scale,
};

export default function CurriculumLibrariesContent() {
  const [activeCategory, setActiveCategory] = useState<LibraryCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState<LibraryCourse | null>(null);
  const [courseModalOpen, setCourseModalOpen] = useState(false);

  const filtered = libraryCourses.filter((c) => {
    const matchesCat = activeCategory === 'all' || c.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || c.title.toLowerCase().includes(q) || c.tags.some(t => t.toLowerCase().includes(q));
    return matchesCat && matchesSearch;
  });

  const stats = [
    { label: 'Specialized Tracks', value: libraryCourses.filter(c => c.category === 'specialized').length, color: 'text-red-600 bg-red-50' },
    { label: 'Premium Courses', value: libraryCourses.filter(c => c.category === 'premium').length, color: 'text-amber-600 bg-amber-50' },
    { label: 'Signature Courses', value: libraryCourses.filter(c => c.category === 'signature').length, color: 'text-rose-600 bg-rose-50' },
    { label: 'Seasonal Programs', value: libraryCourses.filter(c => c.category === 'seasonal').length, color: 'text-orange-600 bg-orange-50' },
  ];

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground">Special Curriculum Libraries</h1>
          <p className="text-sm text-muted-foreground mt-1">Specialized, premium, seasonal, and Professor Signature courses</p>
        </div>
        <div className="flex items-center gap-2 bg-input border border-border rounded-lg px-3 py-2">
          <Search size={15} className="text-muted-foreground shrink-0" />
          <input
            type="text"
            placeholder="Search libraries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-44"
          />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className={`rounded-2xl p-4 border border-border ${s.color.split(' ')[1]} flex items-center gap-3`}>
            <div className={`text-2xl font-extrabold font-tabular ${s.color.split(' ')[0]}`}>{s.value}</div>
            <p className="text-xs font-semibold text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Category tabs */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {categoryTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveCategory(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all shrink-0 ${
              activeCategory === tab.id
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Course grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {filtered.map((course) => (
          <div key={course.id} className="bg-card border border-border rounded-2xl overflow-hidden card-hover flex flex-col">
            {/* Category accent bar */}
            <div className={`h-1.5 w-full ${
              course.category === 'specialized' ? 'bg-gradient-to-r from-red-400 to-rose-500' :
              course.category === 'premium' ? 'bg-gradient-to-r from-amber-400 to-yellow-500' :
              course.category === 'signature'? 'bg-gradient-to-r from-rose-400 to-pink-500' : 'bg-gradient-to-r from-orange-400 to-amber-500'
            }`} />

            <div className="p-5 flex flex-col flex-1">
              {/* Badges */}
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {course.badge && (
                  <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${course.badgeColor}`}>
                    {course.category === 'premium' && <Star size={9} fill="currentColor" />}
                    {course.category === 'signature' && <Crown size={9} />}
                    {course.category === 'seasonal' && <Calendar size={9} />}
                    {course.badge}
                  </span>
                )}
                {course.seasonal && (
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{course.seasonal}</span>
                )}
                {course.isFree ? (
                  <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full font-semibold">Free</span>
                ) : (
                  <span className="text-xs text-foreground bg-muted px-2 py-0.5 rounded-full font-semibold">${course.price}</span>
                )}
              </div>

              <h3 className="text-base font-bold text-foreground mb-2 leading-snug">{course.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-3 line-clamp-2">{course.description}</p>

              {/* Professor signature */}
              {course.professor && (
                <div className="flex items-center gap-2 mb-3 p-2.5 bg-rose-50 border border-rose-100 rounded-xl">
                  <div className="w-7 h-7 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                    <Crown size={12} className="text-rose-600" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-rose-800">{course.professor}</p>
                    <p className="text-xs text-rose-600">{course.professorTitle}</p>
                  </div>
                </div>
              )}

              {/* Meta */}
              <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                <span className="flex items-center gap-1"><Clock size={11} />{course.duration}</span>
                <span className="flex items-center gap-1"><BookOpen size={11} />{course.lessons} lessons</span>
                <span className="flex items-center gap-1"><Star size={11} className="text-amber-500" fill="currentColor" />{course.rating}</span>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-1 mb-4">
                {course.tags.map((tag) => (
                  <span key={tag} className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{tag}</span>
                ))}
              </div>

              {/* CTA */}
              <button
                onClick={() => { setSelectedCourse(course); setCourseModalOpen(true); }}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold btn-scale transition-all ${
                  course.locked
                    ? 'bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100' :'bg-primary text-primary-foreground hover:bg-primary/90'
                }`}
              >
                {course.locked ? <><Lock size={13} /> Unlock Course</> : <><PlayCircle size={13} /> Start Learning</>}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Course Detail Modal */}
      <Modal open={courseModalOpen} onClose={() => setCourseModalOpen(false)} title="Course Details" size="md">
        {selectedCourse && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {selectedCourse.badge && (
                <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${selectedCourse.badgeColor}`}>
                  {selectedCourse.badge}
                </span>
              )}
              {selectedCourse.isFree ? (
                <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full font-semibold">Free</span>
              ) : (
                <span className="text-xs font-bold text-foreground">${selectedCourse.price}</span>
              )}
            </div>
            <h3 className="text-lg font-bold text-foreground">{selectedCourse.title}</h3>
            <p className="text-sm text-muted-foreground">{selectedCourse.description}</p>
            {selectedCourse.professor && (
              <div className="flex items-center gap-3 p-3 bg-rose-50 border border-rose-100 rounded-xl">
                <Crown size={16} className="text-rose-600 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-rose-800">{selectedCourse.professor}</p>
                  <p className="text-xs text-rose-600">{selectedCourse.professorTitle}</p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Duration', value: selectedCourse.duration },
                { label: 'Lessons', value: selectedCourse.lessons },
                { label: 'Rating', value: `★ ${selectedCourse.rating}` },
              ].map((m) => (
                <div key={m.label} className="bg-muted rounded-xl p-3 text-center">
                  <p className="text-base font-bold text-foreground">{m.value}</p>
                  <p className="text-xs text-muted-foreground">{m.label}</p>
                </div>
              ))}
            </div>
            <button className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors">
              {selectedCourse.isFree ? 'Enroll Free' : `Enroll for $${selectedCourse.price}`}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
